import { createAuthenticatedApi, extractApiErrorMessage } from '@/lib/api'
import {
  EmployeeType,
  WorkflowAssignment,
  WorkflowFilterOptions,
  WorkflowStatus,
  WorkStyle,
  WorkStyleAssignment,
  WorkStyleCategory,
} from '@/types/workflow'

type BackendWorkflowStatus = 'PENDING' | 'ASSIGNED' | 'IN_PRODUCTION' | 'READY_FOR_TRIAL' | 'COMPLETED'
type BackendGarmentType = 'JACKET_VEST' | 'SHERWANI' | 'SHIRT' | 'KURTA' | 'TROUSER' | 'KAFTAN' | 'ALTERATION'
type BackendWorkStyleCategory = 'JACKETS' | 'SHIRTS' | 'TROUSERS'
type BackendDesignation =
  | 'JACKET_MAKER'
  | 'SHIRT_MAKER'
  | 'TROUSER_MAKER'
  | 'ALTERATION'
  | 'MASTER'
  | 'SALESMAN'
  | 'PRODUCTION_CONTROLLER'
  | 'ADMINISTRATOR'
  | 'SUPER_ADMIN'
  | 'RECEPTION'

type WorkflowDesignation =
  | BackendDesignation
  | 'Jacket Maker'
  | 'Shirt Maker'
  | 'Trouser Maker'
  | 'Alteration'
  | 'Master'
  | 'Salesman'
  | 'Production Controller'
  | 'Administrator'
  | 'Super Admin'
  | 'Reception'

interface BackendWorkflow {
  id: string
  orderId: string
  garmentId: string
  employeeId: string
  workStyleId: string
  status: BackendWorkflowStatus
  assignedAt: string
  completedAt?: string | null
  paymentAmount?: string | number | null
  notes?: string | null
  employee?: {
    id: string
    fullName: string
    designation: BackendDesignation
  }
  garment?: {
    id: string
    type: BackendGarmentType
    description?: string | null
    subGarmentType?: string | null
    remarks?: string | null
    quantity: number
  }
  workStyle?: {
    id: string
    name: string
    category: BackendWorkStyleCategory
    rate?: string | number | null
  }
}

interface BackendWorkStyle {
  id: string
  name: string
  category: BackendWorkStyleCategory
  rate?: string | number | null
  description?: string | null
}

interface WorkflowListResponse {
  success: boolean
  data: BackendWorkflow[]
}

interface WorkflowMutationResponse {
  success: boolean
  data: BackendWorkflow | BackendWorkflow[]
}

interface WorkflowWorkStyleListResponse {
  success: boolean
  data: BackendWorkStyle[]
}

interface WorkflowByOrderResponse {
  success: boolean
  data: {
    order: {
      id: string
      orderNumber: string
      status: string
      subStatus?: string | null
    }
    workflows: BackendWorkflow[]
  }
}

const api = createAuthenticatedApi()

const backendGarmentTypeMap: Record<BackendGarmentType, string> = {
  JACKET_VEST: 'Jacket & Vest',
  SHERWANI: 'Sherwani',
  SHIRT: 'Shirt',
  KURTA: 'Kurta',
  TROUSER: 'Trouser',
  KAFTAN: 'Kaftans / Nigerian Set',
  ALTERATION: 'Alteration'
}

const backendWorkStyleCategoryMap: Record<BackendWorkStyleCategory, WorkStyleCategory> = {
  JACKETS: WorkStyleCategory.JACKETS,
  SHIRTS: WorkStyleCategory.SHIRTS,
  TROUSERS: WorkStyleCategory.TROUSERS
}

const designationToWorkflowTypeMap: Partial<Record<WorkflowDesignation, EmployeeType>> = {
  JACKET_MAKER: EmployeeType.JACKET_MAKER,
  SHIRT_MAKER: EmployeeType.SHIRT_MAKER,
  TROUSER_MAKER: EmployeeType.TROUSER_MAKER,
  ALTERATION: EmployeeType.ALTERATION,
  'Jacket Maker': EmployeeType.JACKET_MAKER,
  'Shirt Maker': EmployeeType.SHIRT_MAKER,
  'Trouser Maker': EmployeeType.TROUSER_MAKER,
  Alteration: EmployeeType.ALTERATION
}

const mapBackendStatus = (status: BackendWorkflowStatus): WorkflowStatus => {
  switch (status) {
    case 'PENDING':
      return WorkflowStatus.PENDING
    case 'ASSIGNED':
      return WorkflowStatus.ASSIGNED
    case 'IN_PRODUCTION':
      return WorkflowStatus.IN_PRODUCTION
    case 'READY_FOR_TRIAL':
      return WorkflowStatus.READY_FOR_TRIAL
    case 'COMPLETED':
      return WorkflowStatus.COMPLETED
    default:
      return WorkflowStatus.PENDING
  }
}

const mapFrontendStatusToBackend = (status: WorkflowStatus): BackendWorkflowStatus => {
  switch (status) {
    case WorkflowStatus.PENDING:
      return 'PENDING'
    case WorkflowStatus.ASSIGNED:
      return 'ASSIGNED'
    case WorkflowStatus.IN_PRODUCTION:
      return 'IN_PRODUCTION'
    case WorkflowStatus.READY_FOR_TRIAL:
      return 'READY_FOR_TRIAL'
    case WorkflowStatus.COMPLETED:
      return 'COMPLETED'
    default:
      return 'PENDING'
  }
}

const toNumber = (value?: string | number | null): number => {
  if (value === undefined || value === null || value === '') {
    return 0
  }

  return typeof value === 'number' ? value : Number(value)
}

const mapBackendWorkStyleToFrontend = (workStyle: BackendWorkStyle): WorkStyle => ({
  id: workStyle.id,
  name: workStyle.name,
  category: backendWorkStyleCategoryMap[workStyle.category],
  rate: toNumber(workStyle.rate),
  description: workStyle.description || undefined,
})

export const mapDesignationToWorkflowType = (designation: string): EmployeeType | null => {
  return designationToWorkflowTypeMap[designation as WorkflowDesignation] || null
}

const groupBackendWorkflows = (workflows: BackendWorkflow[]): WorkflowAssignment[] => {
  const grouped = new Map<string, WorkflowAssignment>()

  for (const workflow of workflows) {
    const quantity = workflow.garment?.quantity || 1
    const workStyleRate = toNumber(workflow.workStyle?.rate)
    const workStyleAssignment: WorkStyleAssignment[] = workflow.workStyle
      ? [
          {
            id: workflow.workStyle.id,
            workStyleId: workflow.workStyle.id,
            workStyleName: workflow.workStyle.name,
            rate: workStyleRate,
            quantity,
            totalAmount: toNumber(workflow.paymentAmount) || workStyleRate * quantity,
            assignedAt: new Date(workflow.assignedAt),
          },
        ]
      : []

    const groupKey = [
      workflow.orderId,
      workflow.garmentId,
      workflow.employeeId,
      workflow.status,
      workflow.notes || '',
    ].join(':')

    const existing = grouped.get(groupKey)
    if (existing) {
      existing.workflowIds.push(workflow.id)
      existing.workStyles.push(...workStyleAssignment)
      existing.totalPayment += workStyleAssignment.reduce((sum, style) => sum + style.totalAmount, 0)
      if (!existing.completedAt && workflow.completedAt) {
        existing.completedAt = new Date(workflow.completedAt)
      }
      continue
    }

    grouped.set(groupKey, {
      id: workflow.id,
      workflowIds: [workflow.id],
      orderId: workflow.orderId,
      orderItemId: workflow.garmentId,
      garmentType: workflow.garment ? backendGarmentTypeMap[workflow.garment.type] : 'Jacket & Vest',
      quantity,
      employeeId: workflow.employeeId,
      employeeName: workflow.employee?.fullName || 'Unknown Employee',
      employeeType: mapDesignationToWorkflowType(workflow.employee?.designation || '') || EmployeeType.JACKET_MAKER,
      workStyles: workStyleAssignment,
      status: mapBackendStatus(workflow.status),
      assignedAt: workflow.assignedAt ? new Date(workflow.assignedAt) : undefined,
      completedAt: workflow.completedAt ? new Date(workflow.completedAt) : undefined,
      totalPayment: workStyleAssignment.reduce((sum, style) => sum + style.totalAmount, 0),
      notes: workflow.notes || undefined,
    })
  }

  return Array.from(grouped.values())
}

export const getWorkflowAssignments = async (filters?: WorkflowFilterOptions): Promise<WorkflowAssignment[]> => {
  try {
    const params = new URLSearchParams()
    params.append('limit', '1000')

    if (filters?.orderId) params.append('orderId', filters.orderId)
    if (filters?.employeeId) params.append('employeeId', filters.employeeId)
    if (filters?.status) params.append('status', mapFrontendStatusToBackend(filters.status))

    const response = await api.get<WorkflowListResponse>(`/workflows?${params.toString()}`)
    let assignments = groupBackendWorkflows(response.data.data)

    if (filters?.employeeType) {
      assignments = assignments.filter((assignment) => assignment.employeeType === filters.employeeType)
    }

    if (filters?.searchQuery) {
      const query = filters.searchQuery.toLowerCase()
      assignments = assignments.filter((assignment) =>
        assignment.orderId.toLowerCase().includes(query) ||
        assignment.employeeName.toLowerCase().includes(query) ||
        assignment.garmentType.toLowerCase().includes(query)
      )
    }

    return assignments
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to fetch workflow assignments'))
  }
}

export const getWorkflowAssignmentsByOrder = async (orderId: string): Promise<WorkflowAssignment[]> => {
  try {
    const response = await api.get<WorkflowByOrderResponse>(`/workflows/order/${orderId}`)
    return groupBackendWorkflows(response.data.data.workflows)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to fetch order workflow assignments'))
  }
}

export const getWorkflowWorkStyles = async (garmentType?: string): Promise<WorkStyle[]> => {
  try {
    const params = new URLSearchParams()
    if (garmentType) {
      params.append('garmentType', garmentType)
    }

    const query = params.toString()
    const response = await api.get<WorkflowWorkStyleListResponse>(`/workflows/work-styles${query ? `?${query}` : ''}`)
    return response.data.data.map(mapBackendWorkStyleToFrontend)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to fetch workflow work styles'))
  }
}

export const createWorkflowAssignment = async (assignmentData: {
  orderId: string
  orderItemId: string
  garmentType: string
  quantity: number
  employeeId: string
  workStyleIds: string[]
  notes?: string
}): Promise<WorkflowAssignment> => {
  try {
    const response = await api.post<WorkflowMutationResponse>('/workflows', {
      orderId: assignmentData.orderId,
      garmentId: assignmentData.orderItemId,
      employeeId: assignmentData.employeeId,
      workStyleIds: assignmentData.workStyleIds,
      notes: assignmentData.notes,
    })

    const createdWorkflows = Array.isArray(response.data.data) ? response.data.data : [response.data.data]
    return groupBackendWorkflows(createdWorkflows)[0]
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to create workflow assignment'))
  }
}

export const updateWorkflowAssignment = async (assignmentData: {
  id: string
  employeeId?: string
  workStyleIds?: string[]
  status?: WorkflowStatus
  notes?: string
}): Promise<WorkflowAssignment> => {
  try {
    if (assignmentData.workStyleIds && assignmentData.workStyleIds.length > 1) {
      throw new Error('Updating multiple work styles at once is not supported in the current workflow edit contract')
    }

    const response = await api.put<WorkflowMutationResponse>(`/workflows/${assignmentData.id}`, {
      employeeId: assignmentData.employeeId,
      workStyleId: assignmentData.workStyleIds?.[0],
      status: assignmentData.status ? mapFrontendStatusToBackend(assignmentData.status) : undefined,
      notes: assignmentData.notes,
    })

    const updatedWorkflow = Array.isArray(response.data.data) ? response.data.data : [response.data.data]
    return groupBackendWorkflows(updatedWorkflow)[0]
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to update workflow assignment'))
  }
}

export const updateWorkflowAssignmentsBatch = async (
  workflowIds: string[],
  updates: {
    employeeId?: string
    notes?: string
  }
): Promise<void> => {
  try {
    await Promise.all(
      workflowIds.map((workflowId) =>
        api.put(`/workflows/${workflowId}`, {
          employeeId: updates.employeeId,
          notes: updates.notes,
        })
      )
    )
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to update workflow assignments'))
  }
}

export const updateWorkflowAssignmentStatus = async (
  workflowIds: string[],
  status: WorkflowStatus
): Promise<void> => {
  try {
    await Promise.all(
      workflowIds.map((workflowId) =>
        api.put(`/workflows/${workflowId}/status`, {
          status: mapFrontendStatusToBackend(status),
        })
      )
    )
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to update workflow assignment status'))
  }
}
